import { Box, TextField, Typography } from '@material-ui/core';
import { CloudDownload, Edit } from '@material-ui/icons';
import { PDFPreview } from '@roc/client-portal-shared/components';
import { AgGridColumnProps, DataGrid, FileUpload, FilterButton, IconButton, StandardDialog, Toolbar } from '@roc/ui';
import { Button } from '@roc/ui';
import { createSelectField } from '../../../components/formComponents/common/componentBuilder';
import { DateField } from '@roc/ui';
import { _StateLicenseUpload } from '@roc/feature-one-tool';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { LicenseDetailsStore } from '../stores/licenseDetailsStore';
import { CompanyProfileStore } from '../stores/companyProfileStore';

const LICENSE_TYPES = [
  { label: 'Company', value: 'COMPANY' },
  { label: 'Originator', value: 'ORIGINATOR' },
];


const US_STATES = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

const LockedTextField = ({ label, value }: { label: string; value: string }) => (
  <TextField
    label={label}
    value={value}
    disabled
    fullWidth
    variant="outlined"
    InputProps={{ readOnly: true }}
  />
);

const LicenseForm = observer(({ store, isEditMode }: { store: LicenseDetailsStore; isEditMode?: boolean }) => {
  const isOriginator = isEditMode
    ? store.editingLicense?.type === 'ORIGINATOR'
    : store.form.fields.type.value === 'ORIGINATOR';
  const originatorOptions = store.originatorOptions ?? [];
  const [uploadItems, setUploadItems] = useState<FileUpload[]>([]);

  useEffect(() => {
    if (uploadItems?.[0]) {
      store.uploadLicenseDocument(uploadItems[0].file);
    }
  }, [uploadItems]);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      {isEditMode ? (
        <LockedTextField
          label="Type"
          value={store.editingLicense?.type === 'ORIGINATOR' ? 'Originator' : 'Company'}
        />
      ) : createSelectField({
        label: 'Type',
        fieldName: 'type',
        testId: 'type',
        store,
        options: LICENSE_TYPES,
        standaloneLabel: false,
      })}

      {isOriginator && (isEditMode ? (
        <LockedTextField label="Originator" value={store.editingLicense?.originatorFullName ?? ''} />
      ) : createSelectField({
        label: 'Originator *',
        fieldName: 'originator',
        testId: 'originator',
        store,
        options: originatorOptions,
        standaloneLabel: false,
      }))}

      {isEditMode ? (
        <LockedTextField label="State" value={store.editingLicense?.state ?? ''} />
      ) : createSelectField({
        label: 'State',
        fieldName: 'state',
        testId: 'state',
        store,
        options: US_STATES,
        standaloneLabel: false,
      })}

      <Box>
        <DateField
          label="Expiration Date"
          required
          value={store.form.fields.expirationDate.value}
          onChange={date => store.onFieldChange('expirationDate', date)}
          error={!!store.form.fields.expirationDate.error}
          helperText={store.form.fields.expirationDate.error}
          testId="expirationDate"
          disablePast
          inputVariant="outlined"
          fullWidth
          format="MM/dd/yyyy"
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Document{!isEditMode && ' *'}
        </Typography>
        <_StateLicenseUpload
          items={uploadItems}
          setItems={setUploadItems}
          onRemoveItem={() => setUploadItems([])}
        />
      </Box>
    </Box>
  );
});

export const LicenseDetails = observer(({ companyProfileStore } : { companyProfileStore : CompanyProfileStore }) => {
  const store = companyProfileStore.licenseDetailsStore;
  const gridStore = store.gridStore;

  useEffect(() => {
    gridStore.fetchGridData();
  }, []);

  const isEditMode = !!store.editingLicense;

  const columns: AgGridColumnProps[] = [
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      cellRenderer: ({ value }) => value === 'COMPANY' ? 'Company' : 'Originator',
    },
    {
      field: 'originatorFullName',
      headerName: 'Originator',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: 'state',
      headerName: 'State',
      minWidth: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: 'expirationDate',
      headerName: 'Expiration Date',
      minWidth: 150,
    },
    {
      field: 'lenderDocumentId',
      headerName: 'Document',
      maxWidth: 120,
      cellRendererFramework: params => {
        if (!params.node.data) return null;
        const { lenderDocumentId, type, state } = params.node.data;
        if (!lenderDocumentId) return null;
        const title = [type, state].filter(Boolean).join(' - ');
        return (
          <IconButton
            testId={`preview-license-doc-${lenderDocumentId}`}
            onClick={() => store.previewLicenseDocument(lenderDocumentId, title)}
          >
            <CloudDownload color="primary" />
          </IconButton>
        );
      },
    },
    {
      headerName: 'Actions',
      maxWidth: 100,
      cellRendererFramework: params => {
        if (!params.node.data) return null;
        return (
          <IconButton
            testId={`edit-license-${params.node.data.lenderLicenseId}`}
            onClick={() => store.openEditDialog(params.node.data)}
          >
            <Edit color="primary" />
          </IconButton>
        );
      },
    },
  ];


  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    gridStore.setFilterModel(filterModel);
  };

  return (
    <>
      <DataGrid
        toolbar={
          <Toolbar>
            <Box display="flex" justifyContent="flex-end" width="100%" style={{ gap: 8 }}>
              <FilterButton
                onToggleFilters={() => gridStore.toggleFilters()}
                onClearFilters={() => gridStore.resetFilters()}
              />
              <Button
                variant="contained"
                color="primary"
                testId="add-license"
                onClick={() => store.openAddDialog()}
              >
                Add License
              </Button>
            </Box>
          </Toolbar>
        }
        columns={columns}
        rows={gridStore.gridData.data.rows}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={gridStore.isLoading}
        showFilters={gridStore.showFilters}
        sortModel={gridStore.sortModel}
        filterModel={gridStore.filterModel}
        paginationData={gridStore.gridData.meta}
        setPageNumber={gridStore.setPageNumber}
      />

      <StandardDialog
        open={store.isDialogOpen}
        title={isEditMode ? 'Edit License' : 'Add License'}
        handleClose={() => store.closeDialog()}
        dialogContent={
          <LicenseForm store={store} isEditMode={isEditMode} />
        }
        dialogActions={
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              onClick={() => store.closeDialog()}
              color="primary"
              variant="outlined"
              testId="cancel-license"
            >
              Cancel
            </Button>
            <Button
              onClick={() => store.saveLicense()}
              color="primary"
              variant="contained"
              testId="save-license"
            >
              {isEditMode ? 'Apply Changes' : 'Add'}
            </Button>
          </Box>
        }
      />

      <PDFPreview
        open={store.documentPreview != null}
        title={store.documentPreview?.title}
        data={store.documentPreview?.data}
        headers={store.documentPreview?.headers}
        onClose={() => store.closeDocumentPreview()}
      />
    </>
  );
});
